/**
 * Areas Tab Component
 * Displays the areas list with create, export, filter, and table functionality
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { useAreasList, useAreaExport } from '@/hooks/organization';
import { PermissionGuard } from '@/components/layout';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { SECTIONS } from '@/types/sections';


export const AreasTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
  } = useAreasList();

  const { exportAreas, isExporting } = useAreaExport({
    searchText: undefined,
    status: filters.status,
  });

  return (
    <div className="flex flex-col gap-6" data-testid="areas-tab">
      {/* Action buttons */}
      <div className="flex justify-between items-start">
        <PermissionGuard
          action="create"
          fallback="hidden"
          denied={!canSection(SECTIONS.AREAS, 'create')}
        >
          <PrimaryButton iconLeft={<IconScout name="plus" />} onClick={openCreatePanel}>
            {translate('organization:actions.createArea')}
          </PrimaryButton>
        </PermissionGuard>

        <ExportDropdown
          onExport={exportAreas}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <FilterDropdown
          filters={filterConfig}
          values={{
            status: filters.status,
          }}
          onChangeFilter={(key, value) => {
            setFilters((previous) => ({
              ...previous,
              [key]: value,
            }));
          }}
        />
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        searchable
        onSearch={onSearch}
        columnMaxWidth={150}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        data-testid="areas-table"
        isLoading={loading}
        actions={actions}
      />
    </div>
  );
};

export default AreasTab;
